/**
 * CW-5 — format a macro week's date range for the DB-confirmed save banner
 * ("Saved · Week of Jul 21 to 27"). The plan_persist frame (CW-2) carries the
 * week's anchored start/end ISO dates; rendering THOSE (not the LLM's prose
 * week title, which can disagree with the macro calendar) keeps the banner
 * honest about exactly which week was saved.
 *
 * Pure. Parses the ISO date PARTS directly (never `new Date(iso)`), so it's
 * timezone-stable — "2026-07-21" always reads as Jul 21 regardless of locale.
 * Returns null when the start is unparseable (caller falls back to the title).
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** The copy register carries no en or em dash, so a range is joined by the
 *  word (B87 leftovers, 2026-09-08) — the same joiner `useWeeklyPlan`'s week
 *  range, `SeasonGlance` and the demo fixture use. */
const RANGE_JOINER = ' to ';

/** Whole days from a to b. UTC arithmetic on parsed PARTS, so it is
 *  timezone-stable for the same reason the rest of this file is. */
function daysBetween(
  a: { year: number; month: number; day: number },
  b: { year: number; month: number; day: number },
): number {
  return (
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000
  );
}

function parseIso(iso: string | null | undefined): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Round-29 critic 3 — a cross-year span prints its years only once it is
 *  LONGER THAN EIGHT WEEKS, because that is where a span stops reading as one
 *  block of training and could be either of two years.
 *
 *  ⚠️ Round-31 critic F1 (2026-09-10) — this line used to justify itself with
 *  "a training phase is 3 to 8 weeks". NOTHING ENFORCES THAT. The authoring
 *  prompt (`api/lib/planning/macroPlanner.js`) asks for a minimum of two base
 *  weeks and sets no maximum, and `groupPhases` simply runs consecutive weeks
 *  of one mode together, so a 24-week base phase is a legal shape. Eight weeks
 *  is therefore a statement about READABILITY, not a guarantee about phases: a
 *  long enough phase does cross the line, and then it prints its years beside
 *  a shorter phase that prints none. Two shapes in one list is the defect this
 *  threshold was added to remove, so a caller that renders a LIST of ranges
 *  asks `weekRangeNeedsYear` about each and passes `{ year: 'always' }` to all
 *  of them once any one answers true (`MacroPlanPhaseGroups`). */
const YEAR_PRINTS_ABOVE_DAYS = 56;

/** 'auto' — the rule above, and the default: byte-identical for every caller
 *  that passes nothing. 'always' — the caller has already decided this range
 *  belongs to a list that prints years, so both ends print theirs whatever the
 *  span's length. There is no 'never': a span that needs its year keeps it. */
export type WeekRangeYear = 'auto' | 'always';

/**
 * Whether `auto` would print the year for this span. Exported so a caller
 * building a LIST of ranges can ask BEFORE it formats any of them, and give
 * every range in the list one shape (round-31 critic F1). False for a span
 * this helper cannot date at both ends: an undated range prints no year
 * either way.
 */
export function weekRangeNeedsYear(
  startIso: string | null | undefined,
  endIso?: string | null,
): boolean {
  const a = parseIso(startIso);
  const b = parseIso(endIso);
  if (!a || !b) return false;
  return a.year !== b.year && daysBetween(a, b) > YEAR_PRINTS_ABOVE_DAYS;
}

/**
 * "Jul 21 to 27" (same month) · "Jul 28 to Aug 3" (cross-month, and any week
 * or multi-week phase that crosses New Year) · "Sep 7, 2026 to Sep 12, 2027"
 * (a cross-year span longer than eight weeks, or any span the caller asked to
 * print its year) · "Jul 21" (no end).
 * null when `startIso` is missing/invalid.
 */
export function formatWeekRange(
  startIso: string | null | undefined,
  endIso?: string | null,
  options?: { year?: WeekRangeYear },
): string | null {
  const forceYear = options?.year === 'always';
  const a = parseIso(startIso);
  if (!a) return null;
  const b = parseIso(endIso);
  if (!b) {
    return forceYear
      ? `${MONTHS[a.month - 1]} ${a.day}, ${a.year}`
      : `${MONTHS[a.month - 1]} ${a.day}`;
  }
  // B114 (round-17 critic F2, closed by round-22 UX D1) — A LONG SPAN THAT
  // LEAVES ITS YEAR SAYS SO. The helper began life on 7-day spans, where a
  // shared month means a shared year; `MacroPlanPhaseGroups` and `SeasonRail`
  // now pass whole-season spans, and `pickAnchorMeet` caps no horizon, so a
  // championship 12 months out yields 53 weeks. Printing the month twice fixed
  // "Sep 7 to 12" but still read as five days: "Sep 7 to Sep 12" for a span a
  // year long. The year is the only disambiguator, in the house short form
  // `formatRaceDate` uses for a date outside this year ("May 15, 2024").
  //
  // ⚖️ AND ONLY FOR A SPAN LONGER THAN EIGHT WEEKS (round-29 critic 3). Two
  // shapes may not sit in one list: a phase list whose December phase read
  // "Dec 7, 2026 to Jan 17, 2027" beside "Sep 8 to Oct 18" printed the year
  // for the calendar, not for the reader, and the rail head printed years for
  // an ordinary 16-week season. A span that crosses New Year is only ambiguous
  // once it is long enough that it could be either of two years, which starts
  // at season length; a phase, and a week, run forward and read as themselves.
  // (Round-31 critic F1: "a phase is short" is a tendency, not a rule — see
  // YEAR_PRINTS_ABOVE_DAYS above — which is why `year: 'always'` exists.)
  //
  // The 7-day case is also a SEAM: `useWeeklyPlan`'s own kicker formats the
  // same range from `Date`s and is pinned to read "Dec 28 to Jan 3"
  // (`useWeeklyPlan.register.test.ts`). Printing a year here would put two
  // athlete-facing producers of ONE week range in disagreement on the same day.
  // That caller passes no options, so it never reaches the forced branch.
  if (forceYear || (a.year !== b.year && daysBetween(a, b) > YEAR_PRINTS_ABOVE_DAYS)) {
    return (
      `${MONTHS[a.month - 1]} ${a.day}, ${a.year}` +
      `${RANGE_JOINER}${MONTHS[b.month - 1]} ${b.day}, ${b.year}`
    );
  }
  // Inside one year, unchanged: the month prints once when it is shared.
  if (a.month === b.month) {
    return `${MONTHS[a.month - 1]} ${a.day}${RANGE_JOINER}${b.day}`;
  }
  return `${MONTHS[a.month - 1]} ${a.day}${RANGE_JOINER}${MONTHS[b.month - 1]} ${b.day}`;
}
